import { useState, useEffect } from "react";
import { Check, X, Plus } from "lucide-react";

/**
 * Modal popover for picking tags. Used by:
 *  - Lightbox "Tag" button (single item)
 *  - Multi-select bulk tag action
 *
 * Returns the chosen tag array via onApply. Supports custom tag entry.
 */
export default function TagPicker({
  initialTags = [],
  allTags = [],
  title = "Apply tags",
  subtitle = "",
  onApply,
  onClose,
}) {
  const [selected, setSelected] = useState(initialTags);
  const [customTag, setCustomTag] = useState("");

  useEffect(() => {
    setSelected(initialTags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (tag) => {
    setSelected((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const addCustom = () => {
    const t = customTag.trim().toLowerCase();
    if (!t) return;
    if (!selected.includes(t)) setSelected([...selected, t]);
    setCustomTag("");
  };

  // Show all known tags + any newly-added custom ones
  const displayTags = Array.from(new Set([...allTags, ...selected])).sort();

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl p-5 max-h-[80vh] flex flex-col"
        style={{
          background: "var(--bg-root)",
          border: "1px solid var(--border-card-frosted)",
          boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold" style={{ color: "var(--text-heading)" }}>{title}</h3>
            {subtitle && <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1"
            style={{ color: "var(--text-muted)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Custom tag input */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            value={customTag}
            onChange={(e) => setCustomTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
            placeholder="New tag…"
            className="flex-1 text-xs text-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)" }}
          />
          <button
            onClick={addCustom}
            disabled={!customTag.trim()}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>

        {/* Tag chips */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          <div className="flex flex-wrap gap-1.5">
            {displayTags.map((tag) => {
              const on = selected.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() => toggle(tag)}
                  className="text-xs px-2.5 py-1 rounded-full font-semibold transition-colors"
                  style={
                    on
                      ? { background: "#4f46e5", color: "#ffffff" }
                      : {
                          background: "var(--bg-input)",
                          border: "1px solid var(--border-input)",
                          color: "var(--text-secondary)",
                        }
                  }
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 mt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {selected.length} tag{selected.length === 1 ? "" : "s"} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => onApply(selected)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
            >
              <Check className="w-3 h-3" />
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
